import React, { createContext, useContext, useState, useEffect } from "react";
import auth0Service from "../services/authOService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = auth0Service.getAuthToken();
    if (token) {
      auth0Service.setAuthToken(token);

      // Decode the base64 token (your token is just a base64 JSON string)
      try {
        const decoded = JSON.parse(atob(token));
        if (decoded?.user) {
          setUser({
            ...decoded.user,
            accessToken: token,
            idToken: token,
          });
        }
      } catch (err) {
        console.error("Failed to decode token:", err);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await auth0Service.login(email, password);

      if (response.access_token) {
        auth0Service.setAuthToken(response.access_token);
        setUser({
          ...response.user,
          accessToken: response.access_token,
          idToken: response.id_token,
        });
        return response;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await auth0Service.register(name, email, password);

      if (response.success) {
        return response;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      setError(null);
      setLoading(true);
      const response = await auth0Service.loginWithGoogle(credential);

      if (response.access_token) {
        setUser({
          ...response.user,
          accessToken: response.access_token,
          idToken: response.id_token || response.access_token,
        });
        return response;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    auth0Service.logout();
    setUser(null);
    setError(null);
  };

  const sendVerificationCode = async (email) => {
    try {
      setLoading(true);
      return await auth0Service.sendVerificationCode(email);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (email, code, verificationToken) => {
    try {
      setLoading(true);
      const response = await auth0Service.verifyCode(email, code, verificationToken);
      
      if (response.access_token) {
        auth0Service.setAuthToken(response.access_token);
        setUser({
          ...response.user,
          accessToken: response.access_token,
          idToken: response.id_token,
        });
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    sendVerificationCode,
    verifyCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
